import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Divider,
  Form,
  Select,
  Typography,
  message,
} from 'antd';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TutorsOfOrganizationsTable } from './TutorsOfOrganizationsTable';
import { useTutorsOfOrganization } from '../../hooks/useTutors';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useUserOrganizations } from '../../hooks/useOrganizations';
import { useHasRole } from '../../hooks/useHasRole';
import { Roles } from '../../Types/interfaces';
import {
  AddTutorsToOrganizationByEmail,
  GetOrganizationsOfRepresentativeWithoutTutors,
} from '../../helper/apiHelper';
import { validateMultiEmail } from '../../Validators/ValidatorEmail';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { QUERY_KEYS } from '../../Constants/queryKeys';

const TutorManagementComponent = (): JSX.Element => {
  const loggedInUser = useCurrentUser();
  const hasRole = useHasRole();
  const isAuthorized =
    hasRole(Roles.Administrator) || hasRole(Roles.OrganizationRepresentative);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const selectedOrganizationId = Form.useWatch('organization', form);
  const { data: tutors = EMPTY_ARRAY } = useTutorsOfOrganization(
    selectedOrganizationId
  );
  const queryClient = useQueryClient();

  const { data: organizationsWithoutTutors = [] } = useQuery({
    queryKey: [...QUERY_KEYS.ORGANIZATIONS_WITHOUT_TUTORS, loggedInUser.id],
    queryFn: async () => {
      return await GetOrganizationsOfRepresentativeWithoutTutors(
        loggedInUser.id
      );
    },
    enabled: isAuthorized,
  });

  useEffect(() => {
    if (userOrganizations?.length === 1) {
      form.setFieldValue('organization', userOrganizations[0].id);
    }
  }, [form, userOrganizations]);

  const addTutorsMutation = useMutation({
    mutationFn: ({
      organizationId,
      emails,
    }: {
      organizationId: number;
      emails: string[];
    }) => AddTutorsToOrganizationByEmail(organizationId, emails),
    onSuccess: () => {
      messageApi.success(t('tutorManComp.success'));
      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.TUTORS_OF_ORGANIZATIONS,
          Number(selectedOrganizationId),
        ],
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USER });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORGANIZATIONS_WITHOUT_TUTORS,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORGANIZATIONS_TUTORS,
      });

      form.resetFields();
    },
    onError: () => {
      messageApi.error(t('tutorManComp.error'));
    },
  });

  const onAdd = async (): Promise<void> => {
    const organizationId = form.getFieldValue('organization');
    const emails = form.getFieldValue('newTutorEmail') ?? [];

    addTutorsMutation.mutate({ organizationId, emails });
  };

  return (
    <>
      {contextHolder}

      {organizationsWithoutTutors.length != 0 && (
        <>
          <Alert
            message={t('tutorManComp.noTutorsMessage')}
            description={
              <Typography.Paragraph>
                <ul>
                  {organizationsWithoutTutors.map((org) => (
                    <li key={org.id}>
                      <Typography.Text>{org.name}</Typography.Text>
                    </li>
                  ))}
                </ul>
              </Typography.Paragraph>
            }
            type="warning"
            showIcon
          />

          <Divider />
        </>
      )}

      <Form form={form} size="large">
        <Form.Item name="organization">
          <Select
            size="large"
            placeholder={t('tutorManComp.selOrg')}
            options={userOrganizations?.map((org) => ({
              label: org.name,
              value: org.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="newTutorEmail"
          rules={[{ validator: validateMultiEmail }]}
        >
          <Select
            size="large"
            mode="tags"
            placeholder={t('tutorManComp.typeEmailForTutor')}
            options={tutors.map((tutor) => ({
              label: `${tutor.firstName} ${tutor.lastName}`.trim(),
              value: tutor.email,
            }))}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" onClick={onAdd}>
            {t('tutorManComp.addToOrg')}
          </Button>
        </Form.Item>
      </Form>

      <TutorsOfOrganizationsTable />
    </>
  );
};

export default TutorManagementComponent;
